import { useState, useEffect, useCallback } from "react";
import {
  FileStack, RefreshCw, Filter, CheckCircle2, AlertCircle, 
  FileText, CreditCard, Download, Eye, Upload, Plug, 
  ChevronDown, ExternalLink, Search, X, AlertTriangle,
  Building2, Receipt, Link2, FileQuestion, Trash2, FileCheck,
  Bot, Sparkles, Tags, TrendingUp, PieChart, Users, Briefcase,
  Landmark, Fuel, Wifi, Banknote, Home, HelpCircle, UserCheck, RotateCcw,
  Brain, Check, Calculator, ArrowRight, Loader2, Target, Send
} from "lucide-react";
import PennylaneLinkModal from "./PennylaneLinkModal";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Catégories de factures avec couleurs et icônes
const CATEGORIES = {
  FOURNISSEUR: { name: 'Fournisseur', color: 'blue', icon: Building2, keywords: ['LEROY', 'BRICO', 'CEDEO', 'POINT P', 'CASTORAMA', 'SCREWFIX', 'MANUTAN', 'WURTH', 'REXEL', 'SONEPAR', 'IKEA', 'AMAZON'] },
  SOUS_TRAITANT: { name: 'Sous-traitant', color: 'purple', icon: Users, keywords: ['VIREMENT', 'VIR INST', 'EVI ', 'CONSTRUCTION', 'TRAVAUX', 'SASU', 'SARL', ' SAS '] },
  CHARGES_SOCIALES: { name: 'Charges sociales', color: 'orange', icon: UserCheck, keywords: ['URSSAF', 'RETRAITE', 'PREVOYANCE', 'MUTUELLE', 'AG2R', 'MALAKOFF', 'HUMANIS'] },
  IMPOTS_TAXES: { name: 'Impôts & Taxes', color: 'red', icon: Landmark, keywords: ['TRESOR', 'DGFIP', 'IMPOT', 'TVA', 'CFE', 'TAXE', 'DIRECTION GENE'] },
  ENERGIE: { name: 'Énergie & Carburant', color: 'yellow', icon: Fuel, keywords: ['TOTAL', 'SHELL', 'ESSO', 'EDF', 'ENGIE', 'CARBURANT', 'ESSENCE'] },
  TELECOM: { name: 'Télécom & IT', color: 'cyan', icon: Wifi, keywords: ['RINGOVER', 'OVH', 'ORANGE', 'SFR', 'FREE', 'BOUYGUES', 'ZAPIER', 'GOOGLE', 'MICROSOFT', 'APPLE', 'LOOKA'] },
  BANQUE: { name: 'Frais bancaires', color: 'gray', icon: Banknote, keywords: ['FRAIS', 'COMMISSION', 'AGIOS', 'INTERET', 'ETRANGER'] },
  LOYER: { name: 'Loyer & Stockage', color: 'indigo', icon: Home, keywords: ['LOYER', 'SHURGARD', 'LOCATION', 'BAIL'] },
  CLIENT: { name: 'Encaissement client', color: 'green', icon: Receipt, keywords: ['REM CHQ', 'CHEQUE'] },
  AVOIR: { name: 'Avoir / Remboursement', color: 'emerald', icon: RotateCcw, keywords: ['AVOIR', 'REMBOURSEMENT', 'CREDIT'] },
  DIVERS: { name: 'Divers', color: 'slate', icon: HelpCircle, keywords: [] }
};

// Fonction pour détecter la catégorie d'une transaction
const detectCategory = (transaction) => {
  const label = ((transaction.supplier || '') + ' ' + (transaction.description || '')).toUpperCase();
  
  // Encaissements clients (montants positifs avec chèques)
  if (transaction.amount > 0 && (label.includes('REM CHQ') || label.includes('CHEQUE'))) {
    return 'CLIENT';
  }
  
  // Avoirs (montants positifs des fournisseurs)
  if (transaction.amount > 0 && label.includes('AVOIR')) {
    return 'AVOIR';
  }
  
  // Chercher par keywords
  for (const [code, cat] of Object.entries(CATEGORIES)) {
    if (code === 'DIVERS' || code === 'CLIENT' || code === 'AVOIR') continue;
    for (const kw of cat.keywords) {
      if (label.includes(kw)) {
        return code;
      }
    }
  }
  
  return 'DIVERS';
};

export default function ConsolidationComptable() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filtres
  const [supplierFilter, setSupplierFilter] = useState("");
  const [justificatifFilter, setJustificatifFilter] = useState("all"); // all, with, without
  const [sourceFilter, setSourceFilter] = useState("all"); // all, pennylane, cb_only, missing
  const [typeFilter, setTypeFilter] = useState("all"); // all, decaissement, encaissement
  const [categoryFilter, setCategoryFilter] = useState("all"); // all, FOURNISSEUR, SOUS_TRAITANT, etc.
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal fournisseurs API
  const [showApiModal, setShowApiModal] = useState(false);
  
  // IA Rachel
  const [showRachelPanel, setShowRachelPanel] = useState(false);
  const [rachelKnowledge, setRachelKnowledge] = useState(null);
  const [classifiedTransactions, setClassifiedTransactions] = useState({});
  
  // Modal ajout justificatif
  const [showJustificatifModal, setShowJustificatifModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [justificatifFile, setJustificatifFile] = useState(null);
  const [uploadingJustif, setUploadingJustif] = useState(false);
  
  // États pour l'extraction IA du justificatif
  const [extractionStep, setExtractionStep] = useState("upload"); // upload, extracting, preview, saving
  const [extractedData, setExtractedData] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  
  // États pour la classification Rachel
  const [classifyingTransaction, setClassifyingTransaction] = useState(null);
  const [classificationResult, setClassificationResult] = useState(null);
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [learningMapping, setLearningMapping] = useState(false);
  
  // États pour l'attribution de client/chantier
  const [findingClient, setFindingClient] = useState(null);
  const [clientMatchResult, setClientMatchResult] = useState(null);
  const [showClientMatchModal, setShowClientMatchModal] = useState(false);
  const [attributingClient, setAttributingClient] = useState(false);
  
  // États pour l'attribution en batch
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSelections, setBatchSelections] = useState({}); // {transactionId: clientId}
  const [batchAttributing, setBatchAttributing] = useState(false);
  
  // États pour l'upload de factures fournisseurs
  const [showFactureUpload, setShowFactureUpload] = useState(false);
  const [factureFiles, setFactureFiles] = useState([]);
  const [uploadingFactures, setUploadingFactures] = useState(false);
  const [factureUploadResults, setFactureUploadResults] = useState([]);
  
  // Import CB
  const [showImportSection, setShowImportSection] = useState(false);
  const [importedFiles, setImportedFiles] = useState([]);
  const [cbCount, setCbCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  
  // Modal Pennylane Link - Rattachement aux éléments Pennylane
  const [showPennylaneLinkModal, setShowPennylaneLinkModal] = useState(false);
  const [pennylaneLinkTransaction, setPennylaneLinkTransaction] = useState(null);
  
  // Load imported files
  const loadImportedFiles = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/releves/list`);
      const data = await resp.json();
      setImportedFiles(data.files || []);
    } catch (e) {
      console.error("loadImportedFiles error:", e);
    }
  }, []);
  
  // Load CB transaction count
  const loadCbCount = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/rapprochement/cb/transactions`);
      const data = await resp.json();
      setCbCount(data.transactions?.length || 0);
    } catch (e) {
      console.error("loadCbCount error:", e);
    }
  }, []);
  
  // Charger les connaissances de Rachel (mappings appris + règles)
  const loadRachelKnowledge = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/rachel/knowledge`);
      if (resp.ok) {
        const data = await resp.json();
        setRachelKnowledge(data);
        console.log("✅ Rachel knowledge loaded:", data.learned?.stats);
      }
    } catch (e) {
      console.error("loadRachelKnowledge error:", e);
    }
  }, []);
  
  // Classification intelligente avec Rachel
  const classifyWithRachel = useCallback((transaction) => {
    if (!rachelKnowledge) return null;
    
    const label = ((transaction.supplier || '') + ' ' + (transaction.description || '')).toUpperCase();
    const supplierName = (transaction.supplier || '').toUpperCase().trim();
    
    // 1. Vérifier les mappings appris de Rachel (haute priorité)
    const learnedSuppliers = rachelKnowledge.learned?.suppliers || {};
    for (const [normalizedName, mapping] of Object.entries(learnedSuppliers)) {
      if (supplierName.includes(normalizedName) || normalizedName.includes(supplierName) || label.includes(normalizedName)) {
        return {
          category: mapping.categorie,
          compte_charge: mapping.compte_charge,
          compte_tiers: mapping.compte_tiers,
          source: 'rachel_learned',
          confidence: 0.95
        };
      }
    }
    
    // 2. Utiliser les catégories de Rachel (règles de base)
    const categories = rachelKnowledge.categories || [];
    for (const cat of categories) {
      // Vérifier si le label contient un mot-clé de cette catégorie
      // (les keywords sont dans rules_base.json côté backend)
      const catKeywords = getCategoryKeywords(cat.id);
      for (const kw of catKeywords) {
        if (label.includes(kw.toUpperCase())) {
          return {
            category: cat.label,
            compte_charge: cat.compte_charge,
            compte_tiers_prefix: cat.compte_tiers_prefix,
            source: 'rachel_rules',
            confidence: 0.8
          };
        }
      }
    }
    
    return null;
  }, [rachelKnowledge]);
  
  // Keywords par catégorie (synchronisé avec rules_base.json de Rachel)
  const getCategoryKeywords = (categoryId) => {
    const keywordsMap = {
      'fournisseurs_materiaux': ['LEROY MERLIN', 'POINT P', 'CEDEO', 'BRICOMAN', 'CASTORAMA', 'BRICO DEPOT', 'MR BRICOLAGE', 'GEDIMAT', 'BIGMAT', 'PROLIANS'],
      'fournisseurs_plomberie': ['CEDEO', 'RICHARDSON', 'BROSSETTE', 'SANISITT', 'THERMADOR', 'WATTS', 'ROTH', 'COMAP', 'GIACOMINI', 'SAUNIER DUVAL', 'ATLANTIC', 'DAIKIN', 'MITSUBISHI'],
      'fournisseurs_electricite': ['REXEL', 'SONEPAR', 'CGED', 'YESSS', 'LEGRAND', 'SCHNEIDER', 'HAGER', 'ABB', 'GEWISS'],
      'location_materiel': ['KILOUTOU', 'LOXAM', 'HERTZ', 'EUROPCAR', 'RENTOKIL', 'MANITOU', 'SALTI', 'LOCAMOD'],
      'carburant_energie': ['TOTAL', 'TOTALENERGIES', 'SHELL', 'BP', 'ESSO', 'ENI', 'AVIA', 'INTERMARCHE', 'LECLERC', 'CARREFOUR', 'EDF', 'ENGIE', 'ENEDIS'],
      'fournitures_bureau': ['AMAZON', 'AMAZON EU', 'MANUTAN', 'RAJA', 'BRUNEAU', 'OFFICE DEPOT', 'STAPLES', 'FNAC', 'DARTY', 'LDLC', 'MATERIEL.NET'],
      'assurances': ['AXA', 'ALLIANZ', 'MAAF', 'MAIF', 'MACIF', 'GROUPAMA', 'GENERALI', 'MMA', 'MATMUT', 'SMABTP', 'APRIL'],
      'banques_frais': ['FRAIS', 'COMMISSION', 'AGIOS', 'INTERETS DEBITEURS', 'COTISATION CARTE'],
      'telecom': ['ORANGE', 'SFR', 'BOUYGUES', 'FREE', 'OVH', 'RINGOVER', 'AIRCALL'],
      'sous_traitants': ['SARL', 'EURL', 'SAS', 'SASU', 'EI ', 'AUTO ENTREPRENEUR', 'TRAVAUX', 'CHANTIER', 'POSE', 'INSTALLATION'],
      'charges_sociales': ['URSSAF', 'RSI', 'CIPAV', 'CARPIMKO', 'RETRAITE', 'PREVOYANCE', 'MUTUELLE', 'MALAKOFF', 'AG2R', 'HUMANIS', 'KLESIA'],
      'impots_taxes': ['IMPOT', 'TRESOR PUBLIC', 'DGFIP', 'TVA', 'CFE', 'CVAE', 'TAXE'],
      'salaires': ['SALAIRE', 'PAIE', 'VIR SALAIRE', 'REMUNERATION'],
      'honoraires': ['EXPERT COMPTABLE', 'AVOCAT', 'NOTAIRE', 'GEOMETRE', 'ARCHITECTE', 'BUREAU ETUDE', 'CABINET'],
      'restauration': ['RESTAURANT', 'BRASSERIE', 'CAFE', 'HOTEL', 'IBIS', 'NOVOTEL', 'MERCURE', 'MCDONALDS', 'SUBWAY']
    };
    return keywordsMap[categoryId] || [];
  };
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setError(null);
    setUploadSuccess(null);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const resp = await authFetch(`${API}/api/rapprochement/releves/upload`, {
          method: 'POST',
          body: formData
        });
        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.detail || "Erreur upload");
        }
        setUploadSuccess(`✅ ${file.name} importé avec succès (${data.transactions_count || 0} transactions)`);
      } catch (err) {
        setError(`Erreur import ${file.name}: ${err.message}`);
      }
    }
    
    setUploading(false);
    await loadImportedFiles();
    await loadCbCount();
    await loadData(); // Refresh consolidation data
    e.target.value = ''; // Reset input
  };
  
  // Clear all CB transactions
  const clearTransactions = async () => {
    if (!window.confirm("Vider toutes les transactions CB importées ? Cette action est irréversible.")) return;
    try {
      await authFetch(`${API}/api/rapprochement/cb/clear`, { method: 'DELETE' });
      await loadImportedFiles();
      await loadCbCount();
      await loadData();
      setUploadSuccess("Transactions CB vidées");
    } catch (e) {
      setError("Erreur lors de la suppression");
    }
  };
  
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/api/rapprochement/consolidation?`;
      if (supplierFilter) url += `supplier=${encodeURIComponent(supplierFilter)}&`;
      // Note: justificatifFilter est appliqué côté client pour cohérence avec typeFilter
      if (sourceFilter !== "all") url += `source=${sourceFilter}&`;
      
      const resp = await authFetch(url);
      if (!resp.ok) throw new Error("Erreur serveur");
      const result = await resp.json();
      setData(result);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [supplierFilter, sourceFilter]);
  
  // Ouvrir le modal d'ajout de justificatif
  const openJustificatifModal = (transaction) => {
    setSelectedTransaction(transaction);
    setJustificatifFile(null);
    setExtractedData(null);
    setExtractionStep("upload");
    setExtractionError(null);
    setShowJustificatifModal(true);
  };
  
  // Ouvrir le modal Pennylane Link pour rattacher une transaction
  const openPennylaneLinkModal = (transaction) => {
    setPennylaneLinkTransaction(transaction);
    setShowPennylaneLinkModal(true);
  };
  
  // Fermer le modal Pennylane et rafraîchir les données si succès
  const handlePennylaneLinkSuccess = (result) => {
    console.log('Transaction liée à Pennylane:', result);
    setShowPennylaneLinkModal(false);
    setPennylaneLinkTransaction(null);
    // Rafraîchir les données pour voir la mise à jour
    loadData();
  };
  
  // Upload et extraction IA du justificatif
  const handleFileUploadAndExtract = async (file) => {
    if (!file) return;
    
    setJustificatifFile(file);
    setExtractionStep("extracting");
    setExtractionError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const resp = await authFetch(`${API}/api/factures/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.extraction_success && data.extracted_data) {
          setExtractedData(data.extracted_data);
          setExtractionStep("preview");
        } else {
          setExtractionError("Extraction automatique échouée. Vous pouvez saisir manuellement.");
          setExtractedData({
            fournisseur: selectedTransaction?.supplier || '',
            montant_ttc: selectedTransaction?.amount ? Math.abs(selectedTransaction.amount).toString() : '',
            date_facture: selectedTransaction?.date || ''
          });
          setExtractionStep("preview");
        }
      } else {
        const err = await resp.json();
        setExtractionError(err.detail || "Erreur lors de l'extraction");
        setExtractionStep("preview");
        setExtractedData({
          fournisseur: selectedTransaction?.supplier || '',
          montant_ttc: selectedTransaction?.amount ? Math.abs(selectedTransaction.amount).toString() : ''
        });
      }
    } catch (e) {
      setExtractionError(e.message);
      setExtractionStep("upload");
    }
  };
  
  // Sauvegarder le justificatif avec les données extraites
  const handleSaveJustificatif = async () => {
    if (!justificatifFile || !selectedTransaction) return;
    
    setExtractionStep("saving");
    try {
      const formData = new FormData();
      formData.append('file', justificatifFile);
      formData.append('transaction_id', selectedTransaction.id);
      formData.append('transaction_date', selectedTransaction.date);
      formData.append('transaction_amount', selectedTransaction.amount);
      formData.append('supplier', selectedTransaction.supplier || '');
      if (extractedData) {
        formData.append('extracted_data', JSON.stringify(extractedData));
      }
      
      const resp = await authFetch(`${API}/api/rapprochement/justificatif/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (resp.ok) {
        setShowJustificatifModal(false);
        setSelectedTransaction(null);
        setJustificatifFile(null);
        setExtractedData(null);
        setExtractionStep("upload");
        loadData();
      } else {
        const err = await resp.json();
        alert(`Erreur: ${err.detail || 'Sauvegarde échouée'}`);
        setExtractionStep("preview");
      }
    } catch (e) {
      alert(`Erreur: ${e.message}`);
      setExtractionStep("preview");
    }
  };
  
  // Classification avec Rachel
  const handleClassifyWithRachel = async (transaction) => {
    setClassifyingTransaction(transaction.id);
    setClassificationResult(null);
    
    try {
      const resp = await authFetch(`${API}/api/rachel/classify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: `${transaction.supplier || ''} ${transaction.description || ''}`,
          amount: transaction.amount,
          use_ai: true
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setClassificationResult({
          transaction,
          ...data
        });
        setShowClassifyModal(true);
      }
    } catch (e) {
      console.error("Erreur classification:", e);
    }
    setClassifyingTransaction(null);
  };
  
  // Apprendre le mapping avec Rachel
  const handleLearnMapping = async () => {
    if (!classificationResult?.classification) return;
    
    setLearningMapping(true);
    try {
      const resp = await authFetch(`${API}/api/rachel/learn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_name: classificationResult.supplier_name,
          categorie: classificationResult.classification.categorie || classificationResult.classification.category_label,
          compte_charge: classificationResult.classification.compte_charge,
          compte_tiers: classificationResult.classification.compte_tiers || classificationResult.classification.compte_tiers_prefix || '',
          type_tiers: classificationResult.classification.type_tiers || 'fournisseur'
        })
      });
      
      if (resp.ok) {
        alert("✅ Rachel a appris ce mapping !");
        setShowClassifyModal(false);
        setClassificationResult(null);
      }
    } catch (e) {
      console.error("Erreur apprentissage:", e);
    }
    setLearningMapping(false);
  };
  
  // Recherche de client/chantier pour une transaction
  const handleFindClient = async (transaction, useAi = false) => {
    setFindingClient(transaction.id);
    setClientMatchResult(null);
    
    try {
      const resp = await authFetch(`${API}/api/rachel/find-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier: transaction.supplier || '',
          amount: transaction.amount,
          date: transaction.date,
          description: transaction.description || transaction.label || '',
          use_ai: useAi
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setClientMatchResult({
          transaction,
          ...data
        });
        setShowClientMatchModal(true);
      }
    } catch (e) {
      console.error("Erreur recherche client:", e);
    }
    setFindingClient(null);
  };
  
  // Attribuer une facture à un client
  const handleAttributeToClient = async (clientId) => {
    if (!clientMatchResult?.transaction) return;
    
    setAttributingClient(true);
    try {
      const resp = await authFetch(`${API}/api/rachel/attribute-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: clientMatchResult.transaction.id,
          client_id: clientId
        })
      });
      
      if (resp.ok) {
        alert("✅ Facture attribuée au client !");
        setShowClientMatchModal(false);
        setClientMatchResult(null);
        loadData(); // Recharger les données
      }
    } catch (e) {
      console.error("Erreur attribution:", e);
    }
    setAttributingClient(false);
  };
  
  // ═══════════════════════════════════════════════════════════════
  // ATTRIBUTION EN BATCH
  // ═══════════════════════════════════════════════════════════════
  
  const handleBatchAnalysis = async () => {
    setBatchLoading(true);
    setBatchResults(null);
    setBatchSelections({});
    
    try {
      const resp = await authFetch(`${API}/api/rachel/batch-find-clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 50,
          min_probability: 0.25
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setBatchResults(data);
        
        // Pré-sélectionner les matches haute confiance
        const autoSelections = {};
        data.results?.forEach(r => {
          if (r.best_match && r.best_match.probability >= 60) {
            autoSelections[r.transaction.id] = r.best_match.client.client_id;
          }
        });
        setBatchSelections(autoSelections);
      }
    } catch (e) {
      console.error("Erreur batch analysis:", e);
    }
    setBatchLoading(false);
  };
  
  const handleBatchAttribute = async () => {
    const attributions = Object.entries(batchSelections).map(([transactionId, clientId]) => ({
      transaction_id: transactionId,
      client_id: clientId
    }));
    
    if (attributions.length === 0) {
      alert("Aucune attribution sélectionnée");
      return;
    }
    
    setBatchAttributing(true);
    try {
      const resp = await authFetch(`${API}/api/rachel/batch-attribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributions })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        alert(`✅ ${data.attributed} transactions attribuées sur ${data.total}`);
        setShowBatchModal(false);
        setBatchResults(null);
        setBatchSelections({});
        loadData();
      }
    } catch (e) {
      console.error("Erreur batch attribute:", e);
    }
    setBatchAttributing(false);
  };
  
  // ═══════════════════════════════════════════════════════════════
  // UPLOAD DE FACTURES FOURNISSEURS
  // ═══════════════════════════════════════════════════════════════
  
  const handleFactureFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setFactureFiles(files);
    setFactureUploadResults([]);
  };
  
  const handleFacturesUpload = async () => {
    if (factureFiles.length === 0) return;
    
    setUploadingFactures(true);
    const results = [];
    
    for (const file of factureFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const resp = await authFetch(`${API}/api/factures/upload`, {
          method: 'POST',
          body: formData
        });
        
        const data = await resp.json();
        results.push({
          filename: file.name,
          success: resp.ok,
          data: data,
          error: resp.ok ? null : data.detail
        });
      } catch (e) {
        results.push({
          filename: file.name,
          success: false,
          error: e.message
        });
      }
    }
    
    setFactureUploadResults(results);
    setUploadingFactures(false);
    
    // Si au moins une facture uploadée avec succès, recharger les données
    if (results.some(r => r.success)) {
      loadData();
    }
  };
  
  useEffect(() => {
    loadData();
    loadImportedFiles();
    loadCbCount();
    loadRachelKnowledge(); // Charger les connaissances de Rachel
  }, [loadData, loadImportedFiles, loadCbCount, loadRachelKnowledge]);
  
  // Fonction de classification intelligente utilisant Rachel
  const smartDetectCategory = useCallback((transaction) => {
    // 1. Essayer d'abord avec Rachel (mappings appris + règles)
    const rachelResult = classifyWithRachel(transaction);
    if (rachelResult) {
      // Mapper le résultat Rachel vers nos catégories UI
      return mapRachelToUICategory(rachelResult.category);
    }
    
    // 2. Fallback sur les règles locales
    return detectCategory(transaction);
  }, [classifyWithRachel]);
  
  // Mapper les catégories Rachel vers les catégories UI
  const mapRachelToUICategory = (rachelCategory) => {
    if (!rachelCategory) return 'DIVERS';
    const categoryLower = rachelCategory.toLowerCase();
    
    // Mappings Rachel → UI
    if (categoryLower.includes('matéri') || categoryLower.includes('fournisseur')) return 'FOURNISSEUR';
    if (categoryLower.includes('sous-trait') || categoryLower.includes('sous_trait')) return 'SOUS_TRAITANT';
    if (categoryLower.includes('social') || categoryLower.includes('urssaf')) return 'CHARGES_SOCIALES';
    if (categoryLower.includes('impôt') || categoryLower.includes('taxe') || categoryLower.includes('fiscal')) return 'IMPOTS_TAXES';
    if (categoryLower.includes('énergie') || categoryLower.includes('carburant') || categoryLower.includes('essence')) return 'ENERGIE';
    if (categoryLower.includes('télécom') || categoryLower.includes('telecom') || categoryLower.includes('bureau')) return 'TELECOM';
    if (categoryLower.includes('banc') || categoryLower.includes('frais')) return 'BANQUE';
    if (categoryLower.includes('loyer') || categoryLower.includes('location')) return 'LOYER';
    if (categoryLower.includes('assurance')) return 'FOURNISSEUR';
    if (categoryLower.includes('honoraire')) return 'SOUS_TRAITANT';
    if (categoryLower.includes('plomberie') || categoryLower.includes('électricité')) return 'FOURNISSEUR';
    
    return 'DIVERS';
  };
  
  // Ajouter la catégorie à chaque transaction (utilise Rachel si disponible)
  const transactionsWithCategory = (data?.transactions || []).map(t => {
    const rachelClassification = classifyWithRachel(t);
    return {
      ...t,
      category: rachelClassification ? mapRachelToUICategory(rachelClassification.category) : detectCategory(t),
      rachel_classification: rachelClassification, // Garder les détails Rachel pour affichage
    };
  });
  
  // Filtrage local complet (type, justificatif, catégorie, recherche)
  const filteredTransactions = transactionsWithCategory.filter(t => {
    // Filtre par type (décaissement/encaissement)
    if (typeFilter === "decaissement" && t.amount >= 0) return false;
    if (typeFilter === "encaissement" && t.amount < 0) return false;
    
    // Filtre par justificatif (appliqué localement pour cohérence avec le type)
    if (justificatifFilter === "with" && !t.has_justificatif) return false;
    if (justificatifFilter === "without" && t.has_justificatif) return false;
    
    // Filtre par catégorie
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    
    // Filtre par recherche
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.supplier?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.date?.includes(q) ||
      t.amount?.toString().includes(q)
    );
  });
  
  // Calcul des stats par type (basé sur TOUTES les données)
  const allTransactions = transactionsWithCategory;
  const decaissements = allTransactions.filter(t => t.amount < 0);
  const encaissements = allTransactions.filter(t => t.amount >= 0);
  
  const statsDecaissement = {
    total: decaissements.length,
    montant: Math.abs(decaissements.reduce((sum, t) => sum + t.amount, 0)),
    sansJustif: decaissements.filter(t => !t.has_justificatif).length,
    montantSansJustif: Math.abs(decaissements.filter(t => !t.has_justificatif).reduce((sum, t) => sum + t.amount, 0))
  };
  
  const statsEncaissement = {
    total: encaissements.length,
    montant: encaissements.reduce((sum, t) => sum + t.amount, 0),
    sansJustif: encaissements.filter(t => !t.has_justificatif).length,
    montantSansJustif: encaissements.filter(t => !t.has_justificatif).reduce((sum, t) => sum + t.amount, 0)
  };
  
  // Stats par catégorie
  const statsByCategory = Object.keys(CATEGORIES).reduce((acc, code) => {
    const txInCat = allTransactions.filter(t => t.category === code);
    const decInCat = txInCat.filter(t => t.amount < 0);
    acc[code] = {
      total: txInCat.length,
      montant: txInCat.reduce((sum, t) => sum + t.amount, 0),
      decaissements: decInCat.length,
      montantDec: Math.abs(decInCat.reduce((sum, t) => sum + t.amount, 0)),
      sansJustif: txInCat.filter(t => !t.has_justificatif).length
    };
    return acc;
  }, {});
  
  // Nombre de transactions affichées (après tous les filtres)
  const displayedCount = filteredTransactions.length;
  
  // Stats rapides
  const stats = data?.stats || {};
  const suppliers = data?.suppliers || {};
  
  const formatMontant = (val) => {
    if (!val && val !== 0) return "-";
    return parseFloat(val).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <FileStack className="w-7 h-7 text-violet-400" />
            Consolidation Comptable
          </h2>
          <p className="text-slate-400 mt-1">
            Vue unifiée de toutes vos transactions (Pennylane + Relevés CB + Factures)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Bouton Upload Factures */}
          <button
            onClick={() => setShowFactureUpload(!showFactureUpload)}
            className={`px-4 py-2 ${showFactureUpload ? 'bg-amber-600' : 'bg-amber-600/20 text-amber-400 border border-amber-500/30'} rounded-lg flex items-center gap-2 hover:bg-amber-600/80`}
          >
            <Receipt className="w-4 h-4" />
            Factures
          </button>
          {/* Bouton Import CB */}
          <button
            onClick={() => setShowImportSection(!showImportSection)}
            className={`px-4 py-2 ${showImportSection ? 'bg-emerald-600' : 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'} rounded-lg flex items-center gap-2 hover:bg-emerald-600/80`}
          >
            <Upload className="w-4 h-4" />
            Import CB {cbCount > 0 && `(${cbCount})`}
          </button>
          {/* Bouton Attribution en masse */}
          <button
            onClick={() => { setShowBatchModal(true); handleBatchAnalysis(); }}
            className="px-4 py-2 bg-violet-600/20 text-violet-400 border border-violet-500/30 rounded-lg flex items-center gap-2 hover:bg-violet-600/30"
          >
            <Users className="w-4 h-4" />
            Attribution masse
          </button>
          <button
            onClick={() => setShowApiModal(true)}
            className="px-4 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg flex items-center gap-2 hover:bg-blue-600/30"
          >
            <Plug className="w-4 h-4" />
            Connecteurs API
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>
      
      {/* Section Import CB */}
      {showImportSection && (
        <div className="bg-slate-800/50 rounded-xl border border-emerald-500/30 overflow-hidden">
          <div className="p-4 border-b border-slate-700 bg-emerald-500/10">
            <h3 className="font-semibold flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              Import des Relevés CB (PDF)
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-slate-900/50 rounded-xl p-8 border-2 border-dashed border-slate-600 text-center mb-4">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold mb-2">Importer des relevés CB</h4>
              <p className="text-emerald-400 text-sm mb-4">✨ Les données sont persistées en base</p>
              <input 
                type="file" 
                accept=".pdf,.zip" 
                multiple 
                onChange={handleFileUpload} 
                className="hidden" 
                id="cb-file-upload" 
                disabled={uploading}
              />
              <label 
                htmlFor="cb-file-upload" 
                className={`inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Sélectionner des fichiers PDF
                  </>
                )}
              </label>
            </div>
            
            {uploadSuccess && (
              <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400">
                {uploadSuccess}
              </div>
            )}
            
            {importedFiles.length > 0 && (
              <div className="bg-slate-900/30 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-emerald-400" />
                  {importedFiles.length} fichier(s) importé(s)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {importedFiles.map((file, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-700/50 rounded-full text-sm">{file}</span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-slate-400">
                {cbCount > 0 ? `${cbCount} transactions CB en base` : 'Aucune transaction CB importée'}
              </span>
              {cbCount > 0 && (
                <button 
                  onClick={clearTransactions} 
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                  Vider tout
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Section Upload Factures Fournisseurs */}
      {showFactureUpload && (
        <div className="bg-slate-800/50 rounded-xl border border-amber-500/30 overflow-hidden">
          <div className="p-4 border-b border-slate-700 bg-amber-500/10">
            <h3 className="font-semibold flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-400" />
              Ajouter des Factures Fournisseurs
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Les factures seront analysées par l'IA pour extraction automatique et rapprochement
            </p>
          </div>
          <div className="p-6">
            <div className="bg-slate-900/50 rounded-xl p-8 border-2 border-dashed border-slate-600 text-center mb-4">
              <Receipt className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold mb-2">Importer des factures</h4>
              <p className="text-amber-400 text-sm mb-4">
                ✨ Extraction automatique par IA (fournisseur, montant, date...)
              </p>
              <input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png" 
                multiple 
                onChange={handleFactureFileSelect} 
                className="hidden" 
                id="facture-file-upload" 
                disabled={uploadingFactures}
              />
              <label 
                htmlFor="facture-file-upload" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 rounded-lg cursor-pointer"
              >
                <Upload className="w-4 h-4" />
                Sélectionner des factures
              </label>
            </div>
            
            {/* Fichiers sélectionnés */}
            {factureFiles.length > 0 && (
              <div className="bg-slate-900/30 rounded-lg p-4 mb-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  {factureFiles.length} facture(s) sélectionnée(s)
                </h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {factureFiles.map((file, i) => (
                    <span key={i} className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                      {file.name}
                    </span>
                  ))}
                </div>
                <button
                  onClick={handleFacturesUpload}
                  disabled={uploadingFactures}
                  className="w-full px-4 py-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                >
                  {uploadingFactures ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Analyser et importer les factures
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Résultats d'upload */}
            {factureUploadResults.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Résultats de l'import
                </h4>
                {factureUploadResults.map((result, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded-lg flex items-start gap-3 ${
                      result.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'
                    }`}
                  >
                    {result.success ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{result.filename}</p>
                      {result.success && result.data?.facture && (
                        <div className="text-sm text-slate-400 mt-1">
                          <span className="text-amber-400">{result.data.facture.fournisseur || 'Fournisseur inconnu'}</span>
                          {' • '}
                          <span className="text-emerald-400">{formatMontant(result.data.facture.montant_ttc)}</span>
                          {result.data.facture.date_facture && (
                            <span> • {result.data.facture.date_facture}</span>
                          )}
                        </div>
                      )}
                      {!result.success && (
                        <p className="text-sm text-red-400">{result.error}</p>
                      )}
                    </div>
                    {result.success && (
                      <button
                        onClick={() => handleFindClient({
                          id: result.data.facture?._id,
                          supplier: result.data.facture?.fournisseur,
                          amount: result.data.facture?.montant_ttc,
                          date: result.data.facture?.date_facture,
                          description: result.data.facture?.description
                        })}
                        className="px-3 py-1.5 bg-violet-600/20 text-violet-400 border border-violet-500/30 rounded-lg text-sm flex items-center gap-1 hover:bg-violet-600/30"
                      >
                        <Users className="w-3 h-3" />
                        Attribuer
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Stats Décaissements / Encaissements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Carte Décaissements */}
        <div className={`bg-gradient-to-br from-red-500/10 to-orange-500/10 border rounded-xl p-5 transition-all ${typeFilter === 'decaissement' ? 'border-red-500 ring-2 ring-red-500/30' : 'border-red-500/30'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-red-400">DÉCAISSEMENTS</h3>
                <p className="text-xs text-slate-400">Sorties d'argent (achats)</p>
              </div>
            </div>
            {(typeFilter === 'decaissement') && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs rounded">
                {justificatifFilter === 'without' ? 'MANQUANTES' : 'FILTRE ACTIF'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* Total - cliquable */}
            <div 
              className={`bg-slate-900/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800/50 transition-all ${typeFilter === 'decaissement' && justificatifFilter === 'all' ? 'ring-2 ring-red-500/50' : ''}`}
              onClick={() => {
                setTypeFilter('decaissement');
                setJustificatifFilter('all');
              }}
            >
              <p className="text-xs text-slate-400 mb-1">📊 Total</p>
              <p className="text-xl font-bold">{statsDecaissement.total}</p>
              <p className="text-sm text-slate-400">{formatMontant(statsDecaissement.montant)}</p>
            </div>
            {/* Manquantes - cliquable */}
            <div 
              className={`bg-red-500/10 border border-red-500/30 rounded-lg p-3 cursor-pointer hover:bg-red-500/20 transition-all ${typeFilter === 'decaissement' && justificatifFilter === 'without' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => {
                setTypeFilter('decaissement');
                setJustificatifFilter('without');
              }}
            >
              <p className="text-xs text-red-400 mb-1">⚠️ Manquantes</p>
              <p className="text-xl font-bold text-red-400">{statsDecaissement.sansJustif}</p>
              <p className="text-sm text-red-400">{formatMontant(statsDecaissement.montantSansJustif)}</p>
            </div>
          </div>
        </div>
        
        {/* Carte Encaissements */}
        <div className={`bg-gradient-to-br from-emerald-500/10 to-green-500/10 border rounded-xl p-5 transition-all ${typeFilter === 'encaissement' ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-emerald-500/30'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <Receipt className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-emerald-400">ENCAISSEMENTS</h3>
                <p className="text-xs text-slate-400">Entrées d'argent (ventes, remboursements)</p>
              </div>
            </div>
            {(typeFilter === 'encaissement') && (
              <span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded">
                {justificatifFilter === 'without' ? 'MANQUANTES' : 'FILTRE ACTIF'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* Total - cliquable */}
            <div 
              className={`bg-slate-900/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800/50 transition-all ${typeFilter === 'encaissement' && justificatifFilter === 'all' ? 'ring-2 ring-emerald-500/50' : ''}`}
              onClick={() => {
                setTypeFilter('encaissement');
                setJustificatifFilter('all');
              }}
            >
              <p className="text-xs text-slate-400 mb-1">📊 Total</p>
              <p className="text-xl font-bold">{statsEncaissement.total}</p>
              <p className="text-sm text-slate-400">{formatMontant(statsEncaissement.montant)}</p>
            </div>
            {/* Manquantes - cliquable */}
            <div 
              className={`bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 cursor-pointer hover:bg-orange-500/20 transition-all ${typeFilter === 'encaissement' && justificatifFilter === 'without' ? 'ring-2 ring-orange-500' : ''}`}
              onClick={() => {
                setTypeFilter('encaissement');
                setJustificatifFilter('without');
              }}
            >
              <p className="text-xs text-orange-400 mb-1">⚠️ Manquantes</p>
              <p className="text-xl font-bold text-orange-400">{statsEncaissement.sansJustif}</p>
              <p className="text-sm text-orange-400">{formatMontant(statsEncaissement.montantSansJustif)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Panneau Rachel Levy - IA Compta */}
      <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                <span className="text-violet-400">Rachel Levy</span>
                <span className="px-2 py-0.5 bg-violet-500/20 text-violet-300 text-xs rounded">IA Compta</span>
              </h3>
              <p className="text-xs text-slate-400">Classification automatique par type de dépense</p>
            </div>
          </div>
          <button
            onClick={() => setShowRachelPanel(!showRachelPanel)}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg flex items-center gap-2 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            {showRachelPanel ? 'Masquer analyse' : 'Voir analyse'}
          </button>
        </div>
        
        {/* Filtres par catégorie */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
              categoryFilter === "all" 
                ? "bg-violet-600 text-white" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <Tags className="w-3.5 h-3.5" />
            Toutes ({allTransactions.length})
          </button>
          {Object.entries(CATEGORIES).map(([code, cat]) => {
            const catStats = statsByCategory[code];
            if (catStats?.total === 0) return null;
            const Icon = cat.icon;
            const colorClasses = {
              blue: categoryFilter === code ? 'bg-blue-600 text-white' : 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20',
              purple: categoryFilter === code ? 'bg-purple-600 text-white' : 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20',
              orange: categoryFilter === code ? 'bg-orange-600 text-white' : 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20',
              red: categoryFilter === code ? 'bg-red-600 text-white' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20',
              yellow: categoryFilter === code ? 'bg-yellow-600 text-white' : 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20',
              cyan: categoryFilter === code ? 'bg-cyan-600 text-white' : 'bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20',
              gray: categoryFilter === code ? 'bg-gray-600 text-white' : 'bg-gray-500/10 text-gray-400 hover:bg-gray-500/20',
              indigo: categoryFilter === code ? 'bg-indigo-600 text-white' : 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20',
              green: categoryFilter === code ? 'bg-green-600 text-white' : 'bg-green-500/10 text-green-400 hover:bg-green-500/20',
              emerald: categoryFilter === code ? 'bg-emerald-600 text-white' : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
              slate: categoryFilter === code ? 'bg-slate-600 text-white' : 'bg-slate-500/10 text-slate-400 hover:bg-slate-500/20',
            };
            return (
              <button
                key={code}
                onClick={() => setCategoryFilter(categoryFilter === code ? "all" : code)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${colorClasses[cat.color]}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.name} ({catStats?.total || 0})
                {catStats?.sansJustif > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-500/30 text-red-300 rounded text-[10px]">
                    {catStats.sansJustif}⚠️
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Panel d'analyse étendu */}
        {showRachelPanel && (
          <div className="mt-4 pt-4 border-t border-violet-500/30">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(CATEGORIES).map(([code, cat]) => {
                const catStats = statsByCategory[code];
                if (catStats?.total === 0) return null;
                const Icon = cat.icon;
                return (
                  <div 
                    key={code}
                    onClick={() => setCategoryFilter(code)}
                    className={`bg-slate-800/50 rounded-lg p-3 cursor-pointer hover:bg-slate-800 transition-all ${categoryFilter === code ? 'ring-2 ring-violet-500' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium">{cat.name}</span>
                    </div>
                    <div className="text-lg font-bold">{formatMontant(Math.abs(catStats?.montant || 0))}</div>
                    <div className="text-xs text-slate-400">{catStats?.total} transactions</div>
                    {catStats?.sansJustif > 0 && (
                      <div className="text-xs text-red-400 mt-1">⚠️ {catStats.sansJustif} sans justif.</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Stats générales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          icon={FileStack}
          label="Total transactions"
          value={stats.total || 0}
          color="violet"
          onClick={() => setTypeFilter("all")}
        />
        <StatCard
          icon={CreditCard}
          label="Transactions bancaires"
          value={stats.bank_transactions || 0}
          color="cyan"
          onClick={() => setSourceFilter("pennylane_bank")}
        />
        <StatCard
          icon={AlertTriangle}
          label="Manquantes Pennylane"
          value={stats.missing_in_pennylane || 0}
          color="red"
          onClick={() => setSourceFilter("cb_only")}
        />
        <StatCard
          icon={FileQuestion}
          label="Sans justificatif"
          value={stats.without_justificatif || 0}
          subValue={formatMontant(stats.total_without_justif_amount)}
          color="orange"
          onClick={() => setJustificatifFilter("without")}
        />
        <StatCard
          icon={Plug}
          label="Avec API disponible"
          value={stats.with_api_available || 0}
          color="blue"
          onClick={() => setShowApiModal(true)}
        />
      </div>
      
      {/* Filtres */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex flex-wrap items-center gap-4">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg"
            />
          </div>
          
          {/* Fournisseur */}
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg min-w-[180px]"
          >
            <option value="">Tous les fournisseurs</option>
            {Object.keys(suppliers).map(sup => (
              <option key={sup} value={sup}>
                {sup} ({suppliers[sup].count})
              </option>
            ))}
          </select>
          
          {/* Justificatif */}
          <select
            value={justificatifFilter}
            onChange={(e) => setJustificatifFilter(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg"
          >
            <option value="all">Tous (justif.)</option>
            <option value="with">✅ Avec justificatif</option>
            <option value="without">❌ Sans justificatif</option>
          </select>
          
          {/* Source - Boutons de filtre */}
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded-lg p-1">
            <button
              onClick={() => setSourceFilter("all")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                sourceFilter === "all" 
                  ? "bg-violet-600 text-white" 
                  : "text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setSourceFilter("cb_only")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all flex items-center gap-1.5 ${
                sourceFilter === "cb_only" 
                  ? "bg-red-600 text-white" 
                  : "text-red-400 hover:text-white hover:bg-red-600/50"
              }`}
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Manquantes ({stats.missing_in_pennylane || 0})
            </button>
            <button
              onClick={() => setSourceFilter("pennylane_bank")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                sourceFilter === "pennylane_bank" 
                  ? "bg-cyan-600 text-white" 
                  : "text-cyan-400 hover:text-white hover:bg-cyan-600/50"
              }`}
            >
              Bancaires PL
            </button>
            <button
              onClick={() => setSourceFilter("pennylane_only")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                sourceFilter === "pennylane_only" 
                  ? "bg-blue-600 text-white" 
                  : "text-blue-400 hover:text-white hover:bg-blue-600/50"
              }`}
            >
              Factures PL
            </button>
          </div>
          
          {/* Type - Boutons Décaissement/Encaissement */}
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded-lg p-1">
            <button
              onClick={() => setTypeFilter("all")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                typeFilter === "all" 
                  ? "bg-slate-600 text-white" 
                  : "text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              Tous types
            </button>
            <button
              onClick={() => setTypeFilter("decaissement")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all flex items-center gap-1.5 ${
                typeFilter === "decaissement" 
                  ? "bg-red-600 text-white" 
                  : "text-red-400 hover:text-white hover:bg-red-600/50"
              }`}
            >
              <CreditCard className="w-3.5 h-3.5" />
              Décaissements ({statsDecaissement.total})
            </button>
            <button
              onClick={() => setTypeFilter("encaissement")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all flex items-center gap-1.5 ${
                typeFilter === "encaissement" 
                  ? "bg-emerald-600 text-white" 
                  : "text-emerald-400 hover:text-white hover:bg-emerald-600/50"
              }`}
            >
              <Receipt className="w-3.5 h-3.5" />
              Encaissements ({statsEncaissement.total})
            </button>
          </div>
          
          {/* Reset */}
          {(supplierFilter || justificatifFilter !== "all" || sourceFilter !== "all" || typeFilter !== "all" || categoryFilter !== "all" || searchQuery) && (
            <button
              onClick={() => {
                setSupplierFilter("");
                setJustificatifFilter("all");
                setSourceFilter("all");
                setTypeFilter("all");
                setCategoryFilter("all");
                setSearchQuery("");
              }}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1"
            >
              <X className="w-4 h-4" /> Réinitialiser
            </button>
          )}
          
          {/* Indicateur catégorie active */}
          {categoryFilter !== "all" && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-500/20 border border-violet-500/30 rounded-lg">
              <Tags className="w-4 h-4 text-violet-400" />
              <span className="text-sm text-violet-300">{CATEGORIES[categoryFilter]?.name}</span>
              <button onClick={() => setCategoryFilter("all")} className="text-violet-400 hover:text-white">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{error}</span>
        </div>
      )}
      
      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      ) : (
        <div className="bg-slate-800/30 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Fournisseur</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Montant</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Catégorie</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Justificatif</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">API</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Description</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      Aucune transaction trouvée
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((t, idx) => (
                    <TransactionRow 
                      key={t.id || idx} 
                      transaction={t} 
                      onAddJustificatif={openJustificatifModal}
                      onClassify={handleClassifyWithRachel}
                      classifying={classifyingTransaction === t.id}
                      onFindClient={handleFindClient}
                      findingClient={findingClient === t.id}
                      onPennylaneLink={openPennylaneLinkModal}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination info */}
          <div className="bg-slate-800/50 px-4 py-3 border-t border-slate-700 flex items-center justify-between">
            <span className="text-sm text-slate-400">
              {filteredTransactions.length} transactions affichées
            </span>
            <span className="text-sm text-slate-400">
              Total: {formatMontant(filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0))}
            </span>
          </div>
        </div>
      )}
      
      {/* Résumé par fournisseur */}
      <div className="bg-slate-800/30 rounded-xl border border-slate-700 p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-slate-400" />
          Résumé par fournisseur
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(suppliers).slice(0, 12).map(([name, info]) => (
            <SupplierCard 
              key={name} 
              name={name} 
              info={info}
              onClick={() => setSupplierFilter(name)}
            />
          ))}
        </div>
      </div>
      
      {/* Modal API */}
      {showApiModal && (
        <ApiConnectorsModal 
          apis={data?.available_apis || {}}
          suppliers={suppliers}
          onClose={() => setShowApiModal(false)}
        />
      )}
      
      {/* Modal Ajout Justificatif - Version améliorée avec extraction IA */}
      {showJustificatifModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-400" />
                  Ajouter un justificatif
                  {extractionStep === "extracting" && (
                    <span className="px-2 py-0.5 bg-violet-500/20 text-violet-300 text-xs rounded animate-pulse">
                      Extraction IA...
                    </span>
                  )}
                </h3>
                <button 
                  onClick={() => setShowJustificatifModal(false)}
                  className="p-1 hover:bg-slate-800 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Détails de la transaction */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-medium text-slate-400 mb-2">Transaction à justifier :</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-slate-500 text-xs">Date</span>
                    <p className="font-medium">{selectedTransaction.date}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Montant</span>
                    <p className={`font-bold ${selectedTransaction.amount < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {formatMontant(selectedTransaction.amount)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500 text-xs">Fournisseur</span>
                    <p className="font-medium">{selectedTransaction.supplier || 'Non identifié'}</p>
                  </div>
                  {selectedTransaction.description && (
                    <div className="col-span-2">
                      <span className="text-slate-500 text-xs">Description</span>
                      <p className="text-sm text-slate-400">{selectedTransaction.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Étape 1: Upload */}
              {extractionStep === "upload" && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-violet-500/50 transition-colors">
                    <Upload className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                    <p className="text-slate-300 mb-2">Glissez votre facture ici ou cliquez pour sélectionner</p>
                    <p className="text-xs text-slate-500 mb-4">PDF, PNG, JPG acceptés</p>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUploadAndExtract(e.target.files?.[0])}
                      className="w-full max-w-xs mx-auto"
                    />
                  </div>
                  <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 flex items-start gap-3">
                    <Brain className="w-5 h-5 text-violet-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-violet-300 font-medium">Extraction automatique IA</p>
                      <p className="text-xs text-slate-400">Rachel va analyser le document pour extraire automatiquement : fournisseur, montants, dates, n° de facture...</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Étape 2: Extraction en cours */}
              {extractionStep === "extracting" && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 text-violet-400 animate-spin" />
                  <p className="text-violet-300 font-medium">Rachel analyse le document...</p>
                  <p className="text-sm text-slate-500 mt-2">Extraction des informations en cours</p>
                </div>
              )}
              
              {/* Étape 3: Preview des données extraites */}
              {extractionStep === "preview" && extractedData && (
                <div className="space-y-4">
                  {extractionError && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                      <div>
                        <p className="text-sm text-amber-300">{extractionError}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <p className="text-sm text-emerald-300">Fichier analysé : <span className="font-medium">{justificatifFile?.name}</span></p>
                  </div>
                  
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4 text-violet-400" />
                    Données extraites (modifiables)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Fournisseur</label>
                      <input
                        type="text"
                        value={extractedData.fournisseur || ''}
                        onChange={(e) => setExtractedData({...extractedData, fournisseur: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">N° Facture</label>
                      <input
                        type="text"
                        value={extractedData.numero_facture || ''}
                        onChange={(e) => setExtractedData({...extractedData, numero_facture: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Date facture</label>
                      <input
                        type="text"
                        value={extractedData.date_facture || ''}
                        onChange={(e) => setExtractedData({...extractedData, date_facture: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Montant TTC</label>
                      <input
                        type="text"
                        value={extractedData.montant_ttc || ''}
                        onChange={(e) => setExtractedData({...extractedData, montant_ttc: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Montant HT</label>
                      <input
                        type="text"
                        value={extractedData.montant_ht || ''}
                        onChange={(e) => setExtractedData({...extractedData, montant_ht: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">TVA</label>
                      <input
                        type="text"
                        value={extractedData.montant_tva || ''}
                        onChange={(e) => setExtractedData({...extractedData, montant_tva: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Section Pennylane - Rattachement aux entités Pennylane */}
              {extractionStep === "preview" && extractedData && (
                <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <Send className="w-5 h-5 text-orange-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-orange-300">Rattacher à Pennylane</h4>
                        <p className="text-xs text-slate-400">
                          Associer cette transaction à un fournisseur, client ou catégorie Pennylane
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => openPennylaneLinkModal(selectedTransaction)}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg flex items-center gap-2 text-sm font-medium"
                      data-testid="open-pennylane-link-btn"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Lier à Pennylane
                    </button>
                  </div>
                </div>
              )}
              
              {/* Étape 4: Sauvegarde en cours */}
              {extractionStep === "saving" && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 text-emerald-400 animate-spin" />
                  <p className="text-emerald-300 font-medium">Enregistrement en cours...</p>
                </div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-700 flex justify-between items-center sticky bottom-0 bg-slate-900">
              <button
                onClick={() => {
                  if (extractionStep === "preview") {
                    setExtractionStep("upload");
                    setJustificatifFile(null);
                    setExtractedData(null);
                  } else {
                    setShowJustificatifModal(false);
                  }
                }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                {extractionStep === "preview" ? "← Changer de fichier" : "Annuler"}
              </button>
              
              {extractionStep === "preview" && (
                <button
                  onClick={handleSaveJustificatif}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Enregistrer le justificatif
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Classification Rachel */}
      {showClassifyModal && classificationResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg">
            <div className="p-5 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Brain className="w-5 h-5 text-violet-400" />
                  Classification Rachel Levy
                </h3>
                <button 
                  onClick={() => { setShowClassifyModal(false); setClassificationResult(null); }}
                  className="p-1 hover:bg-slate-800 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Transaction analysée */}
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-slate-400 mb-1">Transaction analysée :</p>
                <p className="font-medium">{classificationResult.transaction?.supplier}</p>
                <p className="text-sm text-slate-500">{classificationResult.transaction?.description}</p>
              </div>
              
              {/* Résultat */}
              {classificationResult.classification ? (
                <div className={`rounded-lg p-4 border ${
                  classificationResult.needs_validation 
                    ? 'bg-amber-500/10 border-amber-500/30' 
                    : 'bg-emerald-500/10 border-emerald-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    {classificationResult.needs_validation ? (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    ) : (
                      <Check className="w-5 h-5 text-emerald-400" />
                    )}
                    <span className={classificationResult.needs_validation ? 'text-amber-300' : 'text-emerald-300'}>
                      {classificationResult.needs_validation ? 'À valider' : 'Classification confirmée'}
                    </span>
                    <span className="text-xs text-slate-500 ml-auto">
                      Source: {classificationResult.classification.source}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Fournisseur détecté</p>
                      <p className="font-medium text-blue-400">{classificationResult.supplier_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Catégorie</p>
                      <p className="font-medium text-purple-400">
                        {classificationResult.classification.categorie || classificationResult.classification.category_label}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Compte de charge</p>
                      <p className="font-medium text-emerald-400">{classificationResult.classification.compte_charge}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Compte tiers</p>
                      <p className="font-medium text-amber-400">
                        {classificationResult.classification.compte_tiers || classificationResult.classification.compte_tiers_prefix || '-'}
                      </p>
                    </div>
                  </div>
                  
                  {classificationResult.classification.explication && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-xs text-slate-500">Explication de Rachel :</p>
                      <p className="text-sm text-slate-300">{classificationResult.classification.explication}</p>
                    </div>
                  )}
                  
                  {/* Métadonnées extraites */}
                  {classificationResult.metadata && Object.keys(classificationResult.metadata).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-xs text-slate-500 mb-2">Métadonnées extraites :</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(classificationResult.metadata).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 bg-slate-700 rounded text-xs">
                            {key}: <span className="text-blue-300">{value}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                  <p className="text-slate-400">Classification non déterminée</p>
                </div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowClassifyModal(false); setClassificationResult(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Fermer
              </button>
              {classificationResult.needs_validation && classificationResult.classification && (
                <button
                  onClick={handleLearnMapping}
                  disabled={learningMapping}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {learningMapping ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4" />
                  )}
                  Valider & Rachel apprend
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Attribution Client/Chantier */}
      {showClientMatchModal && clientMatchResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-400" />
                  Attribution Client/Chantier
                </h3>
                <button 
                  onClick={() => { setShowClientMatchModal(false); setClientMatchResult(null); }}
                  className="p-1 hover:bg-slate-800 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Transaction analysée */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-400 mb-2">Transaction analysée :</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Fournisseur</p>
                    <p className="font-medium">{clientMatchResult.transaction?.supplier || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Montant</p>
                    <p className={`font-bold ${clientMatchResult.transaction?.amount < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {formatMontant(clientMatchResult.transaction?.amount)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Description</p>
                    <p className="text-sm text-slate-300">{clientMatchResult.transaction?.description || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Résultats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-400" />
                    Clients potentiels ({clientMatchResult.matches?.length || 0})
                  </h4>
                  <button
                    onClick={() => handleFindClient(clientMatchResult.transaction, true)}
                    className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Sparkles className="w-4 h-4" />
                    Analyse IA
                  </button>
                </div>
                
                {clientMatchResult.matches?.length > 0 ? (
                  <div className="space-y-3">
                    {clientMatchResult.matches.map((match, idx) => (
                      <div 
                        key={match.client.client_id}
                        className={`rounded-xl p-4 border transition-colors ${
                          idx === 0 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : 'bg-slate-800/50 border-slate-700'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium text-lg">{match.client.nom}</p>
                            <p className="text-sm text-slate-400">
                              {match.client.adresse}, {match.client.code_postal} {match.client.ville}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {/* Statut du client */}
                              {(match.client.statut || match.client.etape) && (
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                  (match.client.statut || '').includes('CHANTIER') ? 'bg-emerald-500/20 text-emerald-400' :
                                  (match.client.statut || '').includes('AVANCE_RECUE') ? 'bg-green-500/20 text-green-400' :
                                  (match.client.statut || '').includes('AVANCE_DEMANDEE') ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {match.client.statut || match.client.etape}
                                </span>
                              )}
                              {/* Dates chantier */}
                              {match.client.date_debut_chantier && (
                                <span className="inline-block px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded text-xs">
                                  🏗️ Début: {match.client.date_debut_chantier}
                                </span>
                              )}
                              {/* Type de transaction (virement client) */}
                              {match.type === 'virement_client' && (
                                <span className="inline-block px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                                  💰 Virement client
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${
                              match.probability >= 70 ? 'text-emerald-400' :
                              match.probability >= 40 ? 'text-amber-400' : 'text-slate-400'
                            }`}>
                              {Math.round(match.probability)}%
                            </div>
                            <p className="text-xs text-slate-500">
                              Confiance {match.confidence_level}
                            </p>
                          </div>
                        </div>
                        
                        {/* Raisons avec indicateurs visuels */}
                        <div className="space-y-1.5 mb-3">
                          {match.reasons?.map((reason, rIdx) => (
                            <div key={rIdx} className={`flex items-center gap-2 text-sm ${
                              reason.warning ? 'text-amber-400' : 
                              reason.high_confidence ? 'text-emerald-300' : 'text-slate-300'
                            }`}>
                              {reason.warning ? (
                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                              ) : reason.high_confidence ? (
                                <Target className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <Check className="w-4 h-4 text-emerald-400" />
                              )}
                              <span>{reason.detail}</span>
                              <span className="text-slate-500 text-xs">
                                ({reason.weight > 0 ? '+' : ''}{(reason.weight * 100).toFixed(0)}%)
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Info montant avance (pour virements clients) */}
                        {match.client.montant_avance && (
                          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-2 mb-3">
                            <p className="text-xs text-green-400 flex items-center gap-1">
                              <Receipt className="w-3 h-3" />
                              Avance attendue: {match.client.montant_avance}€
                              {match.client.date_encaissement_avance && (
                                <span className="text-slate-400 ml-2">
                                  (prévu le {match.client.date_encaissement_avance})
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        
                        {/* Analyse IA */}
                        {match.ai_reasoning && (
                          <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mb-3">
                            <p className="text-xs text-violet-400 mb-1 flex items-center gap-1">
                              <Brain className="w-3 h-3" />
                              Analyse de Rachel (IA)
                            </p>
                            <p className="text-sm text-slate-300">{match.ai_reasoning}</p>
                            {match.ai_clues?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {match.ai_clues.map((clue, cIdx) => (
                                  <span key={cIdx} className="px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded text-xs">
                                    {clue}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Bouton attribuer */}
                        <button
                          onClick={() => handleAttributeToClient(match.client.client_id)}
                          disabled={attributingClient}
                          className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                        >
                          {attributingClient ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Attribuer à ce client
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-800/50 rounded-lg p-8 text-center">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                    <p className="text-slate-400">Aucun client correspondant trouvé</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Essayez l'analyse IA pour une recherche plus approfondie
                    </p>
                  </div>
                )}
              </div>
              
              {clientMatchResult.ai_enhanced && (
                <div className="flex items-center gap-2 text-xs text-violet-400">
                  <Sparkles className="w-4 h-4" />
                  Résultats enrichis par l'analyse IA de Rachel
                </div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-700 flex justify-between items-center sticky bottom-0 bg-slate-900">
              <p className="text-sm text-slate-500">
                {clientMatchResult.total_clients_analyzed} clients analysés
              </p>
              <button
                onClick={() => { setShowClientMatchModal(false); setClientMatchResult(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Attribution en Batch */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-violet-400" />
                  Attribution en Masse
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  Rachel analyse vos transactions et propose des attributions clients
                </p>
              </div>
              <button 
                onClick={() => { setShowBatchModal(false); setBatchResults(null); }}
                className="p-2 hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5">
              {batchLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-12 h-12 text-violet-400 animate-spin mb-4" />
                  <p className="text-slate-400">Rachel analyse vos transactions...</p>
                  <p className="text-sm text-slate-500 mt-1">Cela peut prendre quelques secondes</p>
                </div>
              ) : batchResults ? (
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-violet-400">{batchResults.total_analyzed}</div>
                      <div className="text-xs text-slate-400">Analysées</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-slate-500">{batchResults.skipped_general_charges || 0}</div>
                      <div className="text-xs text-slate-400">Charges gén.</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-amber-400">{batchResults.skipped_unknown || 0}</div>
                      <div className="text-xs text-slate-400">Inconnus IA</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-emerald-400">{batchResults.attributable_count || batchResults.with_matches}</div>
                      <div className="text-xs text-slate-400">Attribuables</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-cyan-400">{batchResults.ai_consulted || 0}</div>
                      <div className="text-xs text-slate-400 flex items-center justify-center gap-1">
                        <Sparkles className="w-3 h-3" /> IA consultée
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-pink-400">{batchResults.ai_learned_new || 0}</div>
                      <div className="text-xs text-slate-400 flex items-center justify-center gap-1">
                        <Brain className="w-3 h-3" /> Appris
                      </div>
                    </div>
                  </div>
                  
                  {/* Liste des résultats */}
                  {batchResults.results?.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">🏗️</span>
                          Dépenses Matériaux & Sous-traitants
                        </h4>
                        <span className="text-sm text-slate-400">
                          {Object.keys(batchSelections).length} sélectionnée(s)
                        </span>
                      </div>
                      
                      {batchResults.results.map((result) => (
                        <div 
                          key={result.transaction.id}
                          className={`rounded-lg border p-4 transition-colors ${
                            batchSelections[result.transaction.id] 
                              ? 'bg-emerald-500/10 border-emerald-500/30' 
                              : 'bg-slate-800/50 border-slate-700'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={!!batchSelections[result.transaction.id]}
                              onChange={(e) => {
                                if (e.target.checked && result.best_match) {
                                  setBatchSelections(prev => ({
                                    ...prev,
                                    [result.transaction.id]: result.best_match.client.client_id
                                  }));
                                } else {
                                  setBatchSelections(prev => {
                                    const newSel = { ...prev };
                                    delete newSel[result.transaction.id];
                                    return newSel;
                                  });
                                }
                              }}
                              className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                            />
                            
                            {/* Transaction info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{result.transaction.supplier || 'Inconnu'}</span>
                                <span className={`font-bold ${result.transaction.amount < 0 ? 'text-red-400' : 'text-green-400'}`}>
                                  {formatMontant(result.transaction.amount)}
                                </span>
                                <span className="text-sm text-slate-500">{result.transaction.date}</span>
                                {result.transaction.type === 'virement_client' && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                                    Virement client
                                  </span>
                                )}
                                {result.transaction.category_type === 'materiau' && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                                    🏗️ Matériau
                                  </span>
                                )}
                                {result.transaction.category_type === 'sous_traitant' && (
                                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                    👷 Sous-traitant
                                  </span>
                                )}
                              </div>
                              {result.transaction.description && (
                                <p className="text-sm text-slate-400 truncate mt-1">{result.transaction.description}</p>
                              )}
                            </div>
                            
                            {/* Best match */}
                            {result.best_match && (
                              <div className="text-right flex-shrink-0">
                                <div className="flex items-center gap-2">
                                  <ArrowRight className="w-4 h-4 text-slate-500" />
                                  <div>
                                    <div className="font-medium">{result.best_match.client.nom}</div>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-lg font-bold ${
                                        result.best_match.probability >= 70 ? 'text-emerald-400' :
                                        result.best_match.probability >= 40 ? 'text-amber-400' : 'text-slate-400'
                                      }`}>
                                        {Math.round(result.best_match.probability)}%
                                      </span>
                                      {result.best_match.client.statut && (
                                        <span className="text-xs text-slate-500">{result.best_match.client.statut}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {result.match_count > 1 && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    +{result.match_count - 1} autre(s) possibilité(s)
                                  </p>
                                )}
                                
                                {/* 🧠 ANALYSE RACHEL - Raisons de l'attribution */}
                                {result.best_match.reasons && result.best_match.reasons.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-slate-700 text-left">
                                    <p className="text-xs text-purple-400 font-medium flex items-center gap-1 mb-1">
                                      <Brain className="w-3 h-3" /> Analyse Rachel :
                                    </p>
                                    <ul className="text-xs text-slate-400 space-y-0.5">
                                      {result.best_match.reasons.slice(0, 4).map((reason, idx) => (
                                        <li key={idx} className={`${reason.warning ? 'text-amber-400' : reason.high_confidence ? 'text-emerald-400' : ''}`}>
                                          • {reason.detail}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Section Charges Générales Ignorées */}
                      {batchResults.skipped?.length > 0 && (
                        <div className="mt-6 p-4 bg-slate-800/30 border border-slate-700 rounded-xl">
                          <h4 className="font-medium text-slate-400 mb-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-slate-500" />
                            {batchResults.skipped_general_charges} charges générales ignorées (non attribuables)
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {batchResults.skipped.slice(0, 10).map((skip, idx) => (
                              <span key={idx} className="px-2 py-1 bg-slate-700/50 text-slate-400 rounded text-xs">
                                {skip.supplier || 'Inconnu'} ({skip.reason?.replace('Charge générale identifiée', '').replace('(', '').replace(')', '').trim()})
                              </span>
                            ))}
                            {batchResults.skipped.length > 10 && (
                              <span className="px-2 py-1 text-slate-500 text-xs">
                                +{batchResults.skipped.length - 10} autres
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Section pour les fournisseurs INCONNUS analysés par l'IA */}
                      {batchResults.skipped_unknown_list?.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                          <h4 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                            <Brain className="w-4 h-4" />
                            {batchResults.skipped_unknown} fournisseurs analysés par l'IA (non-BTP)
                          </h4>
                          <p className="text-xs text-amber-400/70 mb-3">
                            Rachel a consulté l'IA pour ces fournisseurs inconnus et les a classifiés comme non-attribuables
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {batchResults.skipped_unknown_list.slice(0, 8).map((skip, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded text-xs flex items-center gap-1"
                                title={skip.reason}
                              >
                                {skip.ai_consulted && <Sparkles className="w-3 h-3" />}
                                {skip.supplier || 'Inconnu'}
                                {skip.ai_confidence && (
                                  <span className="text-amber-400/50 ml-1">
                                    ({Math.round(skip.ai_confidence * 100)}%)
                                  </span>
                                )}
                              </span>
                            ))}
                            {batchResults.skipped_unknown_list.length > 8 && (
                              <span className="px-2 py-1 text-amber-500/70 text-xs">
                                +{batchResults.skipped_unknown_list.length - 8} autres
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Indicateur d'apprentissage IA */}
                      {batchResults.ai_learned_new > 0 && (
                        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-emerald-400 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>
                              Rachel a appris <strong>{batchResults.ai_learned_new}</strong> nouveau(x) fournisseur(s) !
                            </span>
                          </div>
                          {batchResults.learned_this_session?.length > 0 && (
                            <div className="mt-2 text-xs text-emerald-400/70">
                              Appris : {batchResults.learned_this_session.map(l => l.supplier).join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-800/50 rounded-lg p-8 text-center">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                      <p className="text-slate-400">Aucune correspondance trouvée</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Toutes vos transactions sont déjà attribuées ou aucun client ne correspond
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            
            {/* Footer avec actions */}
            <div className="p-5 border-t border-slate-700 flex items-center justify-between bg-slate-900">
              <div className="text-sm text-slate-400">
                {Object.keys(batchSelections).length > 0 ? (
                  <span className="text-emerald-400">
                    {Object.keys(batchSelections).length} attribution(s) prête(s)
                  </span>
                ) : (
                  'Sélectionnez les transactions à attribuer'
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBatchAnalysis}
                  disabled={batchLoading}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${batchLoading ? 'animate-spin' : ''}`} />
                  Relancer l'analyse
                </button>
                <button
                  onClick={handleBatchAttribute}
                  disabled={batchAttributing || Object.keys(batchSelections).length === 0}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg flex items-center gap-2"
                >
                  {batchAttributing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Attribution...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Attribuer ({Object.keys(batchSelections).length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Pennylane Link */}
      {showPennylaneLinkModal && pennylaneLinkTransaction && (
        <PennylaneLinkModal
          transaction={pennylaneLinkTransaction}
          onClose={() => {
            setShowPennylaneLinkModal(false);
            setPennylaneLinkTransaction(null);
          }}
          onSuccess={handlePennylaneLinkSuccess}
          formatAmount={formatMontant}
        />
      )}
    </div>
  );
}

// Composant ligne de transaction
function TransactionRow({ transaction: t, onAddJustificatif, onClassify, classifying, onFindClient, findingClient, onPennylaneLink }) {
  const isMissing = t.source === "cb_only";
  
  // Récupérer la catégorie
  const category = t.category || 'DIVERS';
  const catInfo = CATEGORIES[category] || CATEGORIES.DIVERS;
  const CatIcon = catInfo.icon;
  
  const categoryColorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400',
    red: 'bg-red-500/20 text-red-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
    gray: 'bg-gray-500/20 text-gray-400',
    indigo: 'bg-indigo-500/20 text-indigo-400',
    green: 'bg-green-500/20 text-green-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    slate: 'bg-slate-500/20 text-slate-400',
  };
  
  return (
    <tr className={`hover:bg-slate-800/30 transition-colors ${isMissing ? 'bg-red-500/5' : ''}`}>
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        <div className="flex items-center gap-2">
          {t.date}
          {isMissing && (
            <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded animate-pulse">
              MANQUANT
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{t.supplier || 'Inconnu'}</div>
      </td>
      <td className="px-4 py-3 text-right font-mono">
        <span className={isMissing ? 'text-red-400 font-semibold' : (t.amount < 0 ? 'text-red-400' : 'text-green-400')}>
          {parseFloat(t.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex flex-col items-center gap-1">
          <span className={`px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 ${categoryColorClasses[catInfo.color]}`}>
            <CatIcon className="w-3 h-3" />
            {catInfo.name}
          </span>
          {/* Indicateur si classifié par Rachel */}
          {t.rachel_classification && (
            <span className="text-[10px] text-violet-400 flex items-center gap-0.5" title={`Compte: ${t.rachel_classification.compte_charge || '-'} | Source: ${t.rachel_classification.source}`}>
              <Brain className="w-3 h-3" />
              Rachel
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        {t.has_justificatif ? (
          <span className="flex items-center justify-center gap-1 text-green-400">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-xs">{t.justificatif_source === 'pennylane' ? 'PL' : 'Local'}</span>
          </span>
        ) : (
          <span className="flex items-center justify-center text-orange-400" title="Sans justificatif">
            <AlertCircle className="w-4 h-4" />
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {t.has_api_available ? (
          <Plug className="w-4 h-4 text-blue-400 mx-auto" title={t.api_info?.name} />
        ) : (
          <span className="text-slate-600">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-slate-400 max-w-[200px] truncate" title={t.description}>
        {t.description || '-'}
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          {t.pennylane_data?.pdf_url && (
            <a 
              href={t.pennylane_data.pdf_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
              title="Voir facture"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {/* Bouton classification Rachel */}
          <button
            onClick={() => onClassify && onClassify(t)}
            disabled={classifying}
            className="p-1.5 bg-violet-500/20 text-violet-400 rounded hover:bg-violet-500/30 disabled:opacity-50"
            title="Classifier avec Rachel"
          >
            {classifying ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Brain className="w-4 h-4" />
            )}
          </button>
          {/* Bouton trouver client/chantier */}
          <button
            onClick={() => onFindClient && onFindClient(t, false)}
            disabled={findingClient}
            className="p-1.5 bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30 disabled:opacity-50"
            title="Trouver le client/chantier"
          >
            {findingClient ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Users className="w-4 h-4" />
            )}
          </button>
          {/* Bouton lier à Pennylane */}
          <button
            onClick={() => onPennylaneLink && onPennylaneLink(t)}
            className="p-1.5 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30"
            title="Rattacher à Pennylane (fournisseur, client, catégorie)"
            data-testid={`pennylane-link-row-${t.id}`}
          >
            <Send className="w-4 h-4" />
          </button>
          {!t.has_justificatif && (
            <button
              onClick={() => onAddJustificatif && onAddJustificatif(t)}
              className="p-1.5 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30"
              title="Ajouter justificatif"
            >
              <Upload className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Carte de statistique
function StatCard({ icon: Icon, label, value, subValue, color, onClick }) {
  const colors = {
    violet: "from-violet-500/20 to-violet-600/10 border-violet-500/30 text-violet-400",
    red: "from-red-500/20 to-red-600/10 border-red-500/30 text-red-400",
    orange: "from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
    cyan: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400",
  };
  
  return (
    <div 
      className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 ${onClick ? 'cursor-pointer hover:scale-[1.02] transition-transform' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-sm text-slate-300">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {subValue && <div className="text-sm text-slate-400 mt-1">{subValue}</div>}
    </div>
  );
}

// Carte fournisseur
function SupplierCard({ name, info, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-left hover:bg-slate-700/50 transition-colors w-full"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium truncate">{name}</span>
        {info.has_api && <Plug className="w-4 h-4 text-blue-400 flex-shrink-0" />}
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-slate-400">{info.count} trans.</span>
        <span className="text-emerald-400">{parseFloat(info.total_amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
      </div>
      <div className="flex items-center gap-3 mt-2 text-xs">
        {info.without_justif > 0 && (
          <span className="text-orange-400">{info.without_justif} sans justif.</span>
        )}
        {info.missing_pennylane > 0 && (
          <span className="text-red-400">{info.missing_pennylane} manquantes PL</span>
        )}
      </div>
    </button>
  );
}

// Modal connecteurs API
function ApiConnectorsModal({ apis, suppliers, onClose }) {
  // Fournisseurs qui ont des transactions ET une API disponible
  const relevantApis = Object.entries(apis).filter(([key, _]) => {
    return Object.keys(suppliers).some(sup => sup.toUpperCase().includes(key));
  });
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-slate-700">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Plug className="w-5 h-5 text-blue-400" />
            Connecteurs API Fournisseurs
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <p className="text-slate-400 mb-4">
            Ces fournisseurs proposent des APIs ou des exports automatiques pour récupérer vos factures.
            Vous pouvez les configurer dans Super Batard &gt; Connecteurs.
          </p>
          
          {relevantApis.length > 0 ? (
            <div className="space-y-3">
              <h4 className="font-medium text-emerald-400 mb-2">
                Fournisseurs détectés dans vos transactions :
              </h4>
              {relevantApis.map(([key, info]) => (
                <div key={key} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="font-medium">{info.name}</div>
                        <span className="text-xs text-slate-400 px-2 py-0.5 bg-slate-800 rounded">
                          {info.api_type === 'api' ? 'API REST' : 'Export/Scraping'}
                        </span>
                      </div>
                    </div>
                    <a
                      href={info.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-blue-600/20 text-blue-400 rounded-lg text-sm flex items-center gap-1 hover:bg-blue-600/30"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Site
                    </a>
                  </div>
                  <p className="text-sm text-slate-400">{info.notes}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              Aucun fournisseur avec API détecté dans vos transactions actuelles.
            </p>
          )}
          
          <div className="mt-6 border-t border-slate-700 pt-4">
            <h4 className="font-medium text-slate-300 mb-3">Tous les connecteurs disponibles :</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(apis).map(([key, info]) => (
                <div key={key} className="bg-slate-900/50 rounded-lg px-3 py-2 text-sm">
                  <div className="font-medium">{info.name}</div>
                  <div className="text-xs text-slate-500">{info.api_type}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 bg-slate-900/50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
